import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Calendar } from 'primereact/calendar';
import { localYmd, toYmd, ymdToDate } from '../utils/dateOnly';

// Typed expiry entry used by the purchase order and bill-scan screens: type digits (YYYYMMDD) and the dashes are
// added, or pick from the calendar. `minDate` is the earliest date the pharmacy will accept; `onCommit` fires with
// a complete valid Date (or null while the entry is incomplete) so the form can move on by itself.
export default function ExpiryInput({ value, onChange, onCommit, onKeyDown, disabled, minDate, inputRef, style, id }) {
  const [raw, setRaw]               = useState('');
  const [error, setError]           = useState('');
  const [pickerVisible, setPickerVisible] = useState(false);
  const [pickerPos, setPickerPos]   = useState({ top: 0, left: 0 });
  const internalRef = useRef(null);
  const iconRef     = useRef(null);
  const ref = inputRef || internalRef;

  // Sync display when external value changes (edit load / template load)
  useEffect(() => {
    if (value) {
      const ymd = toYmd(value);
      setRaw(ymd);
      setError('');
    } else {
      setRaw('');
      setError('');
    }
  }, [value]);

  // Close picker when clicking outside
  useEffect(() => {
    if (!pickerVisible) return;
    const handler = (e) => {
      if (iconRef.current && iconRef.current.contains(e.target)) return;
      // close if click is outside the portal calendar
      const portal = document.getElementById('expiry-picker-portal');
      if (portal && portal.contains(e.target)) return;
      setPickerVisible(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerVisible]);

  const formatPartial = (digits) => {
    if (digits.length <= 4) return digits;
    if (digits.length <= 6) return `${digits.slice(0,4)}-${digits.slice(4)}`;
    return `${digits.slice(0,4)}-${digits.slice(4,6)}-${digits.slice(6,8)}`;
  };

  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    const formatted = formatPartial(digits);
    setRaw(formatted);
    setError('');

    if (digits.length === 8) {
      const ymd = `${digits.slice(0,4)}-${digits.slice(4,6)}-${digits.slice(6,8)}`;
      const parsed = ymdToDate(ymd);                        // local midnight
      if (!parsed || localYmd(parsed) !== ymd) {            // also rejects 2029-02-31
        setError('Invalid date');
        onCommit?.(null);
        onChange?.(null);
        return;
      }
      if (minDate) {
        const minYmd = localYmd(new Date(minDate));
        if (ymd < minYmd) {
          setError(`Min date: ${minYmd}`);
          onCommit?.(null);
          onChange?.(null);
          return;
        }
      }
      onCommit?.(parsed);
      onChange?.(parsed);
    } else {
      onCommit?.(null);
      onChange?.(null);
    }
  };

  const openPicker = () => {
    if (disabled) return;
    // Calculate position of icon button relative to viewport for portal placement
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setPickerPos({
        top:  rect.bottom + window.scrollY + 4,
        left: rect.left   + window.scrollX - 220, // align right edge near icon
      });
    }
    setPickerVisible(v => !v);
  };

    const pickerValue = ymdToDate(toYmd(value));

  const handlePickerChange = (e) => {
    if (e.value) {
      const ymd = localYmd(e.value);
      const d = ymdToDate(ymd);
      setRaw(ymd);
      setError('');
      onCommit?.(d);
      onChange?.(d);
      setPickerVisible(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.5rem 2.2rem 0.5rem 0.5rem',
    border: `1px solid ${error ? '#dc3545' : '#ced4da'}`,
    borderRadius: '4px',
    fontSize: '0.875rem',
    backgroundColor: disabled ? '#e9ecef' : '#fff',
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', ...(style || {}) }}>
      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', width: '100%' }}>
        <input
          ref={ref}
          id={id}
          type="text"
          inputMode="numeric"
          value={raw}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          disabled={disabled}
          placeholder="YYYYMMDD"
          maxLength={10}
          style={inputStyle}
        />
        {!disabled && (
          <span
            ref={iconRef}
            onClick={openPicker}
            title="Pick from calendar"
            style={{ position: 'absolute', right: '0.4rem', cursor: 'pointer', color: '#6c757d', lineHeight: 1 }}
          >
            <i className="pi pi-calendar" style={{ fontSize: '0.9rem' }} />
          </span>
        )}
      </div>
      {error && (
        <span style={{ fontSize: '0.72rem', color: '#dc3545', marginTop: '2px', lineHeight: 1.2 }}>
          {error}
        </span>
      )}
      {pickerVisible && !disabled && ReactDOM.createPortal(
        <div
          id="expiry-picker-portal"
          style={{
            position: 'absolute',
            top:  pickerPos.top,
            left: Math.max(4, pickerPos.left),
            zIndex: 99999,
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            borderRadius: '8px',
            background: '#fff',
          }}
        >
          <Calendar
            inline
            value={pickerValue}
            onChange={handlePickerChange}
            dateFormat="yy-mm-dd"
            minDate={minDate}
          />
        </div>,
        document.body
      )}
    </div>
  );
}
